import React from 'react';

import { dealToProduct } from '../../../analytics-library/entry/dealDataToProducts';
import { EventAction, EventArea, sendEvent } from '../../../analytics-library/entry/sendEvent';
import { WidgetInteracted } from '../../../analytics-library/events/WidgetInteracted';
import { apstagAttributes, getAsin } from '../../../sharedModules/apstag/getAttributes';
import { getClickType } from '../../../sharedModules/getClickType';
import { LegacyEventType } from '../../../types/LegacyEventType';
import { ApiProductType } from '../../../types/ProductTypes';
import type { Deal } from '../../types/Deal';
import getPriceType from '../../utils/getPriceType';
import DisplayPrice from '../GenericComponents/DisplayPrice/DisplayPrice';
import { useHawkWidgetContext } from '../GenericComponents/HawkWidget/HawkWidgetProviderContext';
import type { TrackedLinkAttributesProp } from '../GenericComponents/TrackedLink/model';
import getMerchantLinkCTAtext from '../MerchantLinkWidget/getMerchantLinkCTAtext';

import styles from './styles/tabletop-offer-cta.css';

type TabletopOfferCtaProps = {
  deal: Deal;
  activeButton: number;
  setActiveButton: (buttonIndex: number) => void;
  index: number;
  transformToUppercase?: boolean;
};

const TabletopOfferCta: React.FC<TabletopOfferCtaProps> = ({
  deal,
  activeButton,
  setActiveButton,
  index,
  transformToUppercase = false,
}) => {
  const {
    translate,
    showPrice,
    merchantlinkTextType,
    articleUrl,
    linkText,
    editorialPrice,
    site,
    sendAnalytics,
    genericSharedComponents: { TrackedLink },
    dealData,
    widgetType,
    result,
  } = useHawkWidgetContext();

  const isFallbackWithLinkText = Boolean(linkText && deal.product_type === ApiProductType.fallback);
  const isPriceShown = Boolean(showPrice === 'showPrice' || editorialPrice);
  const url = deal.offer.link;

  const trackLinkClick = (): void => {
    sendAnalytics({
      event: {
        clickType: getClickType({
          link: deal.offer.link,
          articleUrl,
          productType: deal.product_type,
        }),
        type: LegacyEventType.CLICK_FROM,
        areaClicked: EventArea.CTA,
      },
      deal,
      product: { url },
      dealData,
    });
    sendEvent(
      new WidgetInteracted({
        area: EventArea.CTA,
        action: EventAction.AFFILIATE_LINK_CLICKED,
        widget: {
          type: widgetType,
          result,
          products: [dealToProduct(deal, dealData.modelInfo)],
        },
      }),
    );
  };

  const ariaLabelModel =
    deal.model && deal.model !== 'Everything' ? deal.model : deal.model_matched.join(',');

  const attributes: TrackedLinkAttributesProp = {
    href: url,
    referrerPolicy: 'no-referrer-when-downgrade',
    className: styles['link'],
    'data-product-key': deal.product_key,
    'data-url': deal.offer.link,
    'data-model-id': deal.model_id,
    'data-match-id': deal.id,
    'data-product-type': deal.product_type,
    'data-link-merchant': deal.merchant.name,
    'data-merchant-id': deal.merchant.id,
    'data-merchant-name': deal.merchant.name,
    'data-merchant-url': deal.merchant.url,
    'data-merchant-network': deal.an,
    'aria-label':
      (ariaLabelModel &&
        deal.merchant.name &&
        translate('ariaLabelText', [ariaLabelModel, deal.merchant.name])) ||
      translate('alternativeLinkText'),
  };

  const asin = getAsin({
    merchantName: deal.merchant.name,
    productKey: deal.product_key,
  });

  if (asin) {
    attributes[apstagAttributes.asin] = asin;
  }

  if (!url || url === '#') {
    return null;
  }

  return (
    <TrackedLink trackLinkClick={trackLinkClick} attributes={attributes}>
      <div
        className={styles['inner']}
        data-last-hovered={String(activeButton === index)}
        onMouseEnter={(): void => {
          setActiveButton(index);
        }}
        data-product-type={deal.product_type}
      >
        <div>
          {deal.offer.price &&
            isPriceShown &&
            !isFallbackWithLinkText &&
            showPrice !== 'stream' && (
              <>
                <DisplayPrice
                  deal={deal}
                  className="merchantlink-transparent-price"
                  type={getPriceType(deal)}
                  wrapInLink={false}
                />{' '}
              </>
            )}
          <span className={transformToUppercase ? styles['uppercase'] : undefined}>
            {getMerchantLinkCTAtext(deal, translate, merchantlinkTextType, isPriceShown, site)}
          </span>
          {deal.offer.price && Number(deal.offer.price) > 0 && showPrice === 'stream' && (
            <>
              {' '}
              <DisplayPrice
                deal={deal}
                className="merchantlink-price"
                type={getPriceType(deal)}
                wrapInLink={false}
              />
            </>
          )}
        </div>
      </div>
    </TrackedLink>
  );
};

export default TabletopOfferCta;
